import { useEffect, useMemo, useState } from "react"
import type { AdminAccess } from "@/features/admin/useAdminAccess"
import {
  emptyAssociationsModerationNavGate,
  type AssociationsModerationNavGate,
} from "@/features/associations/associationsModerationNavGate"
import { isAssociationsUiEnabled } from "@/features/associations/associationsUiEnabled"
import { getUnverifiedMemberships } from "@/lib/api/userAssociationApi"
import { withAccessTokenRetry } from "@/lib/api/withAccessTokenRetry"
import type { TokenResponse } from "@/lib/api/http"

export type UseAssociationsModerationNavGateParams = {
  accessToken: string | null
  setSession: (tokens: TokenResponse) => void
  grantedPolicies: Record<string, boolean> | null
  baseAccess: AdminAccess
}

/**
 * Уточняет доступ к очереди заявок на членство в союзах для модераторов поддерева
 * без глобальных политик `LeninkaArt.Associations.*`.
 */
export const useAssociationsModerationNavGate = ({
  accessToken,
  setSession,
  grantedPolicies,
  baseAccess,
}: UseAssociationsModerationNavGateParams): AssociationsModerationNavGate => {
  const syncGate = useMemo((): AssociationsModerationNavGate | null => {
    if (!isAssociationsUiEnabled) {
      return { ready: true, hasSubtreeModerationAccess: false }
    }
    if (grantedPolicies === null) return null
    if (baseAccess.canListUnverifiedAssociations) {
      return { ready: true, hasSubtreeModerationAccess: false }
    }
    if (!accessToken) {
      return { ready: true, hasSubtreeModerationAccess: false }
    }
    return null
  }, [accessToken, baseAccess.canListUnverifiedAssociations, grantedPolicies])

  const [probeGate, setProbeGate] = useState<AssociationsModerationNavGate>(
    emptyAssociationsModerationNavGate,
  )

  useEffect(() => {
    if (syncGate !== null) return
    if (!accessToken) {
      setProbeGate({ ready: true, hasSubtreeModerationAccess: false })
      return
    }

    let cancelled = false
    setProbeGate(emptyAssociationsModerationNavGate())

    void (async () => {
      try {
        await withAccessTokenRetry(accessToken, setSession, (token) =>
          getUnverifiedMemberships(token),
        )
        if (!cancelled) {
          setProbeGate({ ready: true, hasSubtreeModerationAccess: true })
        }
      } catch {
        if (!cancelled) {
          setProbeGate({ ready: true, hasSubtreeModerationAccess: false })
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [accessToken, setSession, syncGate])

  return syncGate ?? probeGate
}
